import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCalendarAlt, FaLeaf, FaMapMarkerAlt, FaChurch, FaClock, FaMapMarked, FaChevronDown, FaClipboard, FaClipboardCheck, FaCopy, FaCalendarPlus, FaRegCalendarPlus, FaLocationArrow } from 'react-icons/fa';
import { MdLocationPin } from "react-icons/md";
import { gallery14 } from '../assets';
import Bcalogo from '../assets/bcalogo';
import SlidingImages from './SlidingImages';
import CountdownInline from './CountdownInline.js';

const Details = ({ eventType }) => {
  // Venue coordinates
  const venueLocation = {
    lat: 34.0522,
    lng: -118.2437,
    address: "Jl. Jend. Sudirman No.232A, Bandung"
  };

  const addToCalendar = () => {
    // Wedding date
    const weddingDate = '20260411'; // April 11, 2026

    // Determine event details based on eventType
    let title, startTime, endTime, description, location;

    if (eventType === 'ceremony') {
      title = 'Caleb & Alyssa - Holy Matrimony';
      startTime = 'T100000'; // 10:00 AM
      endTime = 'T120000';   // 12:00 PM
      description = 'Join us for our wedding ceremony';
      location = 'GII Hok Im Tong Gardujati, Bandung';

    } else {
      title = 'Caleb & Alyssa - Wedding Reception';
      startTime = 'T180000'; // 6:00 PM
      endTime = 'T200000';   // 8:00 PM
      description = 'Celebrate with us at our wedding reception';
      location = 'Royal Dynasty, Bandung';
    }

    // Build dates
    const start = weddingDate + startTime;
    const end = weddingDate + endTime;

    // Google Calendar URL
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${start}/${end}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}&ctz=Asia/Jakarta`;

    window.open(url, '_blank');
  };

  // Show ceremony details
  const showCeremony = eventType === 'ceremony' || eventType === 'both';
  // Show reception details
  const showReception = eventType === 'reception' || eventType === 'both';

  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText("3790077619");
      setCopied(true);

      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy", err);
    }
  }

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const circleVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };


  return (
    <section className="py-32 px-8 bg-cream">
      <div className="max-w-6xl mx-auto">
        <div className="mb-20">
          <div className="mb-20">
            <motion.div
              initial={{ opacity:   0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <p className='text-sm mb-4'>Save the date</p>
              <h2 className="text-5xl md:text-7xl mb-10">Saturday, <br /> 11<sup><small>th</small></sup> April 2026</h2>
              <CountdownInline />
              <button
                onClick={addToCalendar}
                className="flex items-center justify-center my-10 mx-auto gap-2 px-6 py-3 bg-sage text-white rounded-full font-medium hover:bg-white hover:text-sage-dark transition-all shadow-lg"
              >
                <FaRegCalendarPlus />
                Add to Calendar
              </button>
            </motion.div>

            <SlidingImages />

            {showCeremony && (
              <motion.div
                className={`holy-matrimony max-w-lg mx-auto p-6 md:p-12 bg-white rounded-2xl shadow-lg text-center relative ${showReception ? 'mb-24' : ''}`}
                initial={{ x: -30, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: "easeOut" }}>
                <p className="text-4xl text-pink font-display font-medium mb-6">
                  Holy Matrimony
                </p>
                <hr className="max-w-48 mx-auto border-b border-dashed border-slate-400 mb-6" />
                <div className="text-base text-sage-dark mb-4">
                  <p className='inline text-xl text-sage'>GII Hok Im Tong Gardujati</p>
                </div>
                <p className="text-base text-charcoal mb-4">Jl. Gardujati No.51, Bandung</p>
                <div className="text-base text-charcoal mb-6">
                  <FaClock className='inline mr-2' />
                  <p className='inline'>10.00 AM</p>
                </div>
                <a
                  href="https://maps.google.com/maps/dir//GII+Hok+Im+Tong+-+Gardujati+Jl.+Gardujati+No.51+Kebon+Jeruk+Andir,+Bandung+City,+West+Java+40181/@-6.918472,107.598232,16z/data=!4m5!4m4!1m0!1m2!1m1!1s0x2e68e63d53ef6bed:0x66d91dc54495d304"
                  target="_blank"
                  rel="noopener noreferrer"
                  className=" inline-flex items-center justify-center gap-2 px-10 py-4 bg-sage text-white font-medium rounded-full shadow-xl hover:bg-white hover:text-sage-dark transition-all duration-300 hover:-translate-y-1 text-sm tracking-wider"
                >
                  <MdLocationPin className="text-lg" />
                  <span>Direction to venue</span>
                </a>
              </motion.div>
            )}

            {showReception && (
              <motion.div
                className="max-w-lg mx-auto p-8 md:p-12 bg-white rounded-2xl shadow-lg text-center relative"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}>
                <p className="text-4xl text-pink font-display font-medium mb-6">
                  Reception
                </p>
                <hr className="max-w-48 mx-auto border-b border-dashed border-slate-400 mb-6" />
                <div className="text-base text-sage-dark mb-4">
                  <p className='inline text-xl text-sage'>Royal Dynasty</p>
                </div>
                <p className='text-base text-charcoal mb-4'>Jl. Jend. Sudirman No.232A, Bandung</p>
                <div className="text-base text-charcoal mb-8">
                  <FaClock className='inline mr-2' />
                  <p className='inline'>18.00 PM</p>
                </div>
                <a
                  href="https://www.google.com/maps/dir//Royal+Dynasty+Restaurant+Gedung+Graha+Sudirman+Jl.+Jend.+Sudirman+No.232A+Lt.1+Kb.+Jeruk,+Kec.+Andir,+Kota+Bandung,+Jawa+Barat+40181/@-6.9196718,107.5955529,16z/data=!4m5!4m4!1m0!1m2!1m1!1s0x2e68e62293188bb1:0x8f8acf36b26965b5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className=" inline-flex items-center justify-center gap-2 px-10 py-4 bg-sage text-white font-medium rounded-full shadow-xl hover:bg-white hover:text-sage-dark transition-all duration-300 hover:-translate-y-1 text-sm tracking-wider"
                >
                  <MdLocationPin className="text-lg" />
                  <span>Direction to venue</span>
                </a>
              </motion.div>
            )}
          </div>
        </div>

        <motion.div
          className="max-w-lg mx-auto text-center transition-all duration-300 relative mb-20"
        >
          <h2 className='text-4xl mb-4'>Dresscode</h2>
          <motion.div
            className="flex space-x-4 items-center justify-center my-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div
              variants={circleVariants}
              className="w-12 h-12 bg-[#d3c6bd] rounded-full shadow-md"
            />

            <motion.div
              variants={circleVariants}
              className="w-12 h-12 bg-[#eae1dc] rounded-full shadow-md"
            />

            <motion.div
              variants={circleVariants}
              className="w-12 h-12 bg-[#f1ece6] rounded-full shadow-md"
            />

            <motion.div
              variants={circleVariants}
              className="w-12 h-12 bg-white rounded-full shadow-md"
            />
          </motion.div>
          <p className='text-sm mb-4'>Neutral Tones <br/ >(Nude, Champagne & White)</p>
        </motion.div>

        <motion.div
          className="max-w-lg mx-auto text-center hover:-translate-y-2 relative"
          initial={{ x: 50, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <h2 className='text-4xl mb-6'>Wedding Gift</h2>
          <p className='text-sm mb-1'>Your presence means the world to us.</p>
          <p className='text-sm mb-6'> But if you’d like to share your love in another way, please refer to the details below</p>
          <button onClick={() => setOpen(prev => !prev)} className='px-8 py-2 shadow bg-sage text-white hover:text-sage-dark rounded-full mb-8 flex items-center gap-2 mx-auto hover:bg-white hover:-translate-y-2 transition-all duration-300'>
            Bank account
            <motion.span
              className="inline-block"
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <FaChevronDown size={16} />
            </motion.span>
          </button>

          <AnimatePresence initial={false}>
            {open && (
              <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="mt-4">
                <div className='bg-white rounded-3xl shadow-lg p-4'>
                  <p className='mb-4 font-bold'>Caleb Ignatius</p>
                  <div className="flex gap-4 items-center justify-center mb-4">
                    <Bcalogo width={75} />
                    3790077619
                  </div>
                  <button
                    onClick={handleCopy}
                    className="py-2 px-6 text-xs border border-sage rounded-lg bg-white/20 hover:bg-white/30 transition"
                  >
                    <p className='inline-block text-pink'>Copy</p>
                  </button>
                  {copied && (
                      <motion.span
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="
                          mt-2 px-4 py-2 text-xs
                          rounded-full
                          bg-white/20 hover:bg-white/30
                          transition
                          block
                        "
                      >
                        Copied to clipboard
                      </motion.span>
                    )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Details;
